import { useQueryClient } from "@tanstack/react-query"
import round from "lodash/round"

import { Toast } from "../../components/Toast"
import WizardHeader from "../../components/WizardHeader"
import WizardCard from "../../components/WizardCard"
import WizardFooter from "../../components/WizardFooter"
import { SubmitButton } from "../../components/SubmitButton"
import LiabilityInsuranceQuoteCard from "./LiabilityInsuranceQuoteCard"
import { useAccountId, useUpdateInsuranceWizard } from "../../hooks"
import { genericErrMsg } from "../../api/auth"
import { formatCurrency } from "../../utils"
import { LiabilityInsuranceTypes } from "@/types/liabilityInsuranceWizard"
import { AccountTypes } from "@/types/account"

export interface LiabilityInsuranceQuoteProps {
  handlePrevious: () => void
  handleNext: () => void
  insuranceWizardData: LiabilityInsuranceTypes
  account: AccountTypes
}

const LiabilityInsuranceQuote = ({
  handlePrevious,
  handleNext,
  insuranceWizardData,
  account,
}: LiabilityInsuranceQuoteProps) => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()

  const acreage = round(insuranceWizardData?.acreage)

  const generalPremium = insuranceWizardData?.general_liability_price_dollars

  const { mutateAsync: updateWizard, isPending: isUpdatingWizard } =
    useUpdateInsuranceWizard(queryClient, accountId, {
      onSuccess: () => {
        handleNext()
      },
      onError: (error: { detail: string }) => {
        Toast.error(error?.detail || genericErrMsg)
      },
    })

  const handleSubmit = async () => {
    await updateWizard({
      wizard_key: "insurance",
      implementation_option: "ins_general_liability",
    })
  }

  return (
    <div
      className="pt-10 lg:pt-13 pb-31"
      data-testid="liability-insurance-quote"
    >
      <WizardHeader
        eyebrow="Your NCX Guide"
        heading="Your Insurance Quote"
        text="Review your coverage options and pricing"
      />

      <div className="container grid grid-cols-1 w-[752px] max-w-full gap-6 px-5 lg:px-10 mt-10 mx-auto">
        <LiabilityInsuranceQuoteCard
          eyebrow="Insurance Coverage"
          heading="Comprehensive General Liability"
          subheading="Essential protection for all landowners"
          list={[
            "Limit of Liability: $1,000,000 Each Occurrence, $2,000,000 Aggregate",
            "Deductible: $250 per occurrence",
            "Covers injuries to visitors on your property",
            "Protection from property damage claims",
            "Legal defense coverage included",
            "Comprehensive coverage for wide range of activities excluding hunting",
          ]}
          annualPremium={formatCurrency(generalPremium, true)}
          summary={`${acreage} acres × $0.32/acre`}
        />

        <WizardCard>
          <div className="p-6">
            <h3
              className="flex items-center justify-between text-charcoal-500 text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
              data-testid="total-anual-premium-header"
            >
              <span>Total Annual Premium</span>

              <span className="text-leaf text-4xl">
                {formatCurrency(generalPremium, true)}
              </span>
            </h3>

            {!account?.is_member ? (
              <p
                className="flex items-center justify-between gap-3 text-dusk-500 font-body leading-[140%] tracking-[0.32px] mt-3"
                data-testid="ncx-membership-fee"
              >
                <span>NCX Membership Fee</span>

                <span>$12/billed monthly</span>
              </p>
            ) : null}

            <p
              className="flex items-center justify-between gap-3 text-dusk-500 font-body leading-[140%] tracking-[0.32px] mt-3"
              data-testid="coverage-text"
            >
              <span>Coverage for {acreage} acres</span>

              <span>per year</span>
            </p>
          </div>
        </WizardCard>
      </div>

      <WizardFooter>
        <div className="flex gap-4 ml-auto">
          <button
            type="button"
            className="liability-insurance-quote-go-back-button btn2 btn2-outline-primary font-bold tracking-[0.32px] whitespace-nowrap"
            onClick={handlePrevious}
            data-testid="go-back-button"
          >
            Go Back
          </button>

          <SubmitButton
            className="liability-insurance-quote-get-next-button btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
            isSubmitting={isUpdatingWizard}
            onClick={() => {
              handleSubmit()
            }}
            data-testid="next-button"
          >
            Next
          </SubmitButton>
        </div>
      </WizardFooter>
    </div>
  )
}

export default LiabilityInsuranceQuote
